import React, { useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  calculateItemPrice,
  calculateSubTotal,
  calculateTotalMRP,
  calculateTotalDiscount,
  calculateDiscountPercentage,
  calculateTotal
} from '../../utils/calculations';
import { PHARMACY_IDS } from '../../config/api';

function DraftPreview({ draft, onClose }) {
  const toast = useToast();
  console.log(draft.additional_charges);
  const contentRef = useRef(null);
  const [showButtons, setShowButtons] = useState(true);

  const handleDownload = async () => {
    setShowButtons(false);
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const content = contentRef.current;
      const canvas = await html2canvas(content, {
        scale: 2,
        backgroundColor: '#ffffff',
        // width: content.offsetWidth,
        windowWidth: content.offsetWidth,
        x: -20,
        width: content.offsetWidth + 40
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 595; // A4 width in points (standard PDF width)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const pdf = new jsPDF({
        orientation: imgHeight > imgWidth ? 'portrait' : 'landscape',
        unit: 'pt',
        format: [imgWidth, imgHeight]
      });

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`Order_${draft.id}.pdf`);
    } finally {
      setShowButtons(true);
    }
  };

  const handleCopy = async () => {
    // Hide buttons before capturing
    setShowButtons(false);

    // Wait for re-render
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const content = contentRef.current;
      const canvas = await html2canvas(content, {
        scale: 2,
        backgroundColor: '#ffffff'
      });

      const blob = await new Promise(resolve => canvas.toBlob(resolve));
      const item = new ClipboardItem({ "image/png": blob });
      await navigator.clipboard.write([item]);

      toast.success('Order details copied to clipboard.');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Could not copy. Please try again.');
    } finally {
      // Show buttons again after capture
      setShowButtons(true);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto" style={{ maxWidth: '1000px' }}>
        <div ref={contentRef} className="p-6 space-y-6">
          {/* Header */}
          <div className="flex justify-between items-start border-b pb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Order #{draft._id || draft.id || 'New'}
              </h2>
              <p className="text-gray-600">
                Date: {draft.createdAt ? new Date(draft.createdAt).toLocaleDateString() : 'Not set'}
              </p>
              {draft.pharmacy && (
                <p className="text-gray-600">
                  Pharmacy: {Object.keys(PHARMACY_IDS).find(key => PHARMACY_IDS[key] === draft.pharmacy).toLowerCase() || 'Unknown Pharmacy'}
                </p>
              )}
              {(draft.partner || draft.custom_partner || draft.customPartner || draft.poc_name) && (
                <p className="text-gray-600">
                  Partner Code: {draft.partner}
                  {(draft.poc_name || draft.pocName) && ` (POC: ${draft.poc_name || draft.pocName})`}
                </p>
              )}
            </div>
            {showButtons && (
              <div className="flex space-x-3">
                <button
                  onClick={handleCopy}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <i className="material-icons-outlined text-base">content_copy</i>
                  <span>Copy</span>
                </button>
                <button
                  onClick={handleDownload}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <i className="material-icons-outlined text-base">download</i>
                  <span>Download PDF</span>
                </button>
                <button
                  onClick={onClose}
                  className="btn-primary flex items-center space-x-2"
                >
                  <i className="material-icons-outlined text-base">close</i>
                  <span>Close</span>
                </button>
              </div>
            )}
          </div>

          {/* Customer Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Customer Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{draft.customer?.name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Mobile</p>
                <p className="font-medium">{draft.customer?.mobile || '-'}</p>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-sm text-gray-600">Address</p>
              <p className="font-medium">{draft.customer?.address_details?.address || '-'}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                {draft.customer?.address_details?.latitude && draft.customer?.address_details?.longitude ? (
                  <div>
                    <p className="text-sm text-gray-600">Coordinates (Hyper Local)</p>
                    <p className="font-medium">
                      {draft.customer.address_details.latitude}, {draft.customer.address_details.longitude}
                    </p>
                  </div>
                ) : draft.customer?.address_details?.pincode ? (
                  <div>
                    <p className="text-sm text-gray-600">Pincode (Pan India)</p>
                    <p className="font-medium">{draft.customer.address_details.pincode}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600">Location</p>
                    <p className="font-medium">-</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[35%]">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Pack Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Quantity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Discount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[20%]">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {draft.items.map((item, index) => (
                  <tr key={index} className="border-b py-2">
                    <td className="px-6 py-4 text-sm break-words">
                      <div className="flex justify-between">
                        <div>
                          <p className="font-medium">{item.medicine_name}</p>
                          <p className="text-sm text-gray-600">
                            {item.manufacturer}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{item.packing_size || '-'}</td>
                    <td className="px-6 py-4 text-sm">{item.quantity}</td>
                    <td className="px-6 py-4 text-sm">₹{item.price}</td>
                    <td className="px-6 py-4 text-sm">{item.discount}%</td>
                    <td className="px-6 py-4 text-sm">₹{calculateItemPrice(item).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mt-6">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Total MRP:</span>
                <span>₹{calculateTotalMRP(draft.items).toFixed(2)}</span>
              </div>

              <div className="flex justify-between text-green-600">
                <span>Total Discount:</span>
                <span>-₹{calculateTotalDiscount(draft.items).toFixed(2)} ({calculateDiscountPercentage(draft.items)}%)</span>
              </div>

              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>₹{calculateSubTotal(draft.items).toFixed(2)}</span>
              </div>

              {draft.additional_charges && draft.additional_charges.map(charge => (
                <div key={charge.id} className="flex justify-between">
                  <span>{charge.name}:</span>
                  <span>₹{charge.amount}</span>
                </div>
              ))}

              <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2 text-gray-800">
                <span>Total Amount:</span>
                <span>₹{calculateTotal(draft.items, draft.additional_charges)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DraftPreview; 